import { participantBadgeGeometry } from "./media/participant-cloud.ts";

type VoiceKind = "microphone" | "headphones";
type Values = {
  live: number;
  voice: number;
  separation: number;
  textWidth: number;
  microphone: number;
  headphones: number;
};
export type BadgeState = {
  key: string;
  current: Values;
  target: Values;
  from: Values;
  voiceKinds: VoiceKind[];
  targetSize: number;
  connecting: boolean;
  started: number;
  sizeUntil: number;
  frame: number | null;
};
export type BadgeTarget = {
  size: number;
  screen: boolean;
  textWidth: number;
  microphoneOff: boolean;
  notListening: boolean;
  connecting: boolean;
};
export type BadgeRefs = {
  root: HTMLButtonElement;
  visual: HTMLSpanElement;
  live: HTMLSpanElement;
  voice: HTMLSpanElement;
  liveSvg: SVGSVGElement;
  voiceSvg: SVGSVGElement;
  curve: SVGPathElement;
  liveBackground: SVGPathElement;
  textPath: SVGTextPathElement;
  voiceBackground: SVGPathElement;
  divider: SVGPathElement;
  connectingIndicator: HTMLSpanElement;
  voiceOff: HTMLSpanElement;
  notListening: HTMLSpanElement;
};
export function createBadgeState(): BadgeState {
  const zero = { live: 0, voice: 0, separation: 0, textWidth: 0, microphone: 0, headphones: 0 };
  return {
    key: "",
    current: { ...zero },
    target: { ...zero },
    from: { ...zero },
    voiceKinds: [],
    targetSize: 128,
    connecting: false,
    started: 0,
    sizeUntil: 0,
    frame: null,
  };
}

// Exact legacy interpolation and mask geometry, operating only on component refs.
function positionBadgeIcon(element: HTMLElement, point: { x: number; y: number }) {
  element.style.left = `${point.x}px`;
  element.style.top = `${point.y}px`;
}

function drawParticipantBadges(refs: BadgeRefs, state: BadgeState) {
  const {
    root,
    visual,
    live,
    voice,
    liveSvg,
    voiceSvg,
    curve,
    liveBackground,
    textPath,
    voiceBackground,
    divider,
    connectingIndicator,
    voiceOff,
    notListening,
  } = refs;
  const size = root.clientWidth || state.targetSize;
  const badges = participantBadgeGeometry(size, {
    hasScreen: state.current.live > 0 || state.target.live > 0,
    liveTextWidth: state.current.textWidth,
    microphoneOff: state.voiceKinds.includes("microphone"),
    notListening: state.voiceKinds.includes("headphones"),
    liveProgress: state.current.live,
    voiceProgress: state.current.voice,
    voiceSeparation: state.current.separation,
    connecting: state.connecting,
  });
  visual.style.maskImage = badges.maskImage;
  live.hidden = !badges.live || state.current.live <= 0;
  voice.hidden = !badges.voice || state.current.voice <= 0;
  if (badges.live) {
    const svg = liveSvg;
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    curve.setAttribute("d", badges.live.textPath);
    liveBackground.setAttribute("d", badges.live.path);
    textPath.setAttribute("startOffset", String(badges.live.textOffset));
    live.style.transformOrigin = `${badges.live.anchor.x}px ${badges.live.anchor.y}px`;
    live.style.transform = `scale(${state.current.live})`;
    live.style.opacity = String(state.current.live);
  }
  if (badges.voice) {
    const svg = voiceSvg;
    const path = voiceBackground;
    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    path.setAttribute("d", badges.voice.path);
    connectingIndicator.hidden = !state.connecting;
    positionBadgeIcon(connectingIndicator, badges.voice.anchor);
    divider.style.display = badges.voice.divider ? "" : "none";
    if (badges.voice.divider) {
      const [from, to] = badges.voice.divider;
      divider.setAttribute("d", `M ${from.x} ${from.y} L ${to.x} ${to.y}`);
    }
    voiceOff.hidden = state.connecting || !state.voiceKinds.includes("microphone");
    notListening.hidden = state.connecting || !state.voiceKinds.includes("headphones");
    voiceOff.style.transform = `translate(-50%, -50%) scale(${state.current.microphone})`;
    notListening.style.transform = `translate(-50%, -50%) scale(${state.current.headphones})`;
    voiceOff.style.opacity = String(state.current.microphone);
    notListening.style.opacity = String(state.current.headphones);
    for (const [index, kind] of badges.voiceKinds.entries()) {
      positionBadgeIcon(kind === "microphone" ? voiceOff : notListening, badges.voice.icons[index]);
    }
    voice.style.transformOrigin = `${badges.voice.anchor.x}px ${badges.voice.anchor.y}px`;
    voice.style.transform = `scale(${state.current.voice})`;
    voice.style.opacity = String(state.current.voice);
  }
}

export function animateParticipantBadges(
  refs: BadgeRefs,
  state: BadgeState,
  { size, screen, textWidth, microphoneOff, notListening, connecting }: BadgeTarget,
) {
  const { root } = refs;
  microphoneOff = !connecting && microphoneOff;
  notListening = !connecting && notListening;
  const key = `${size}-${Number(screen)}-${Number(microphoneOff)}-${Number(notListening)}-${textWidth}-${connecting}`;
  if (state.key === key) return;
  const now = performance.now();
  if (!state.key) state.current.textWidth = textWidth;
  if (state.frame) cancelAnimationFrame(state.frame);
  state.connecting = connecting;
  const voiceVisible = connecting || microphoneOff || notListening;
  const wasVoiceVisible = state.current.voice > 0 || state.target.voice > 0;
  if (voiceVisible && !wasVoiceVisible) {
    state.current.microphone = Number(microphoneOff);
    state.current.headphones = Number(notListening);
  }
  state.voiceKinds = [
    (state.current.microphone || microphoneOff) && "microphone",
    (state.current.headphones || notListening) && "headphones",
  ].filter((kind): kind is VoiceKind => Boolean(kind));
  state.key = key;
  state.targetSize = size;
  state.from = { ...state.current };
  state.target = {
    live: Number(screen),
    voice: Number(voiceVisible),
    separation: microphoneOff && notListening ? 28 : 0,
    textWidth: screen ? textWidth : state.current.textWidth,
    microphone: voiceVisible ? Number(microphoneOff) : state.current.microphone,
    headphones: voiceVisible ? Number(notListening) : state.current.headphones,
  };
  state.started = now;
  state.sizeUntil = now + 550;
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion) {
    state.current = { ...state.target };
    state.voiceKinds = [
      state.target.voice && state.target.microphone && "microphone",
      state.target.voice && state.target.headphones && "headphones",
    ].filter((kind): kind is VoiceKind => Boolean(kind));
    drawParticipantBadges(refs, state);
    state.frame = requestAnimationFrame(() => {
      if (root.isConnected) drawParticipantBadges(refs, state);
    });
    return;
  }
  const tick = (time: number) => {
    if (!root.isConnected) return;
    const progress = Math.min(1, (time - state.started) / 300);
    const eased = 1 - (1 - progress) ** 3;
    for (const property of [
      "live",
      "voice",
      "separation",
      "textWidth",
      "microphone",
      "headphones",
    ] as const) {
      state.current[property] =
        state.from[property] + (state.target[property] - state.from[property]) * eased;
    }
    drawParticipantBadges(refs, state);
    if (
      progress < 1 ||
      (time < state.sizeUntil && Math.abs(root.clientWidth - state.targetSize) > 1)
    ) {
      state.frame = requestAnimationFrame(tick);
    } else {
      state.frame = null;
      state.voiceKinds = [
        state.target.voice && state.target.microphone && "microphone",
        state.target.voice && state.target.headphones && "headphones",
      ].filter((kind): kind is VoiceKind => Boolean(kind));
      if (!state.target.voice) {
        state.current.microphone = 0;
        state.current.headphones = 0;
        state.target.microphone = 0;
        state.target.headphones = 0;
      }
    }
  };
  drawParticipantBadges(refs, state);
  state.frame = requestAnimationFrame(tick);
}
